/**
 * AI Sales Copilot — Reply Suggestion Panel (Session 4).
 *
 * Suggestion-only UI shown directly above the message composer in
 * InboxPage. The agent reviews the suggestion, optionally edits it,
 * then sends it via the regular composer. NOTHING is auto-sent.
 *
 * Rendered only when the company has enabled AI Copilot AND reply
 * suggestions. Both flags are loaded once per session via
 * `/api/ai-copilot/settings` and cached in localStorage so we don't
 * blow up the inbox load with an extra round-trip.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Sparkles, RefreshCw, AlertTriangle, ShieldAlert, BookOpen, ShoppingBag,
  User, Truck, X, Loader2,
} from 'lucide-react';
import axios from 'axios';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const SOURCE_LABELS = {
  knowledge_base:        { icon: BookOpen,   label: 'Knowledge Base' },
  customer_profile:      { icon: User,       label: 'Customer'       },
  order_history:         { icon: ShoppingBag, label: 'Order'         },
  delivery_intelligence: { icon: Truck,      label: 'Delivery Intel' },
  ai_settings:           { icon: Sparkles,   label: 'Brand Settings' },
};

function confidenceTone(c) {
  if (c >= 80) return { bg: 'bg-emerald-100', text: 'text-emerald-800',
                        label: 'High confidence' };
  if (c >= 50) return { bg: 'bg-amber-100',   text: 'text-amber-800',
                        label: 'Medium confidence' };
  return                { bg: 'bg-rose-100',    text: 'text-rose-800',
                          label: 'Low confidence' };
}

export default function ReplySuggestionPanel({
  conversationId,
  onInsertSuggestion,
  authToken,
}) {
  // Cached flag: do we even SHOW the trigger?
  const [enabled, setEnabled] = useState(() => {
    const raw = localStorage.getItem('ai_copilot_enabled');
    return raw === 'true';
  });
  const [enabledKnown, setEnabledKnown] = useState(false);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [suggestion, setSuggestion] = useState(null);

  // Resolve enabled state once per mount (cheap GET, cached for next time).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/ai-copilot/settings`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (cancelled) return;
        const s = res.data || {};
        const flag = !!(s.enabled && (s.reply_suggestions_enabled ?? true));
        setEnabled(flag);
        localStorage.setItem('ai_copilot_enabled', String(flag));
      } catch {
        // Settings fetch failed (permission, network) — don't break inbox.
        setEnabled(false);
      } finally {
        if (!cancelled) setEnabledKnown(true);
      }
    })();
    return () => { cancelled = true; };
  }, [authToken]);

  // Clear panel when switching conversations.
  useEffect(() => {
    setOpen(false);
    setSuggestion(null);
    setError('');
  }, [conversationId]);

  const requestSuggestion = useCallback(async () => {
    if (!conversationId) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(
        `${API_BASE}/ai-copilot/reply-suggestion`,
        { conversation_id: conversationId },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      setSuggestion(res.data);
      setOpen(true);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Failed to generate suggestion';
      setError(detail);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  }, [conversationId, authToken]);

  if (!enabledKnown) return null;
  if (!enabled) return null;

  const conf = suggestion ? confidenceTone(suggestion.confidence ?? 0) : null;
  const flags = suggestion?.safety_flags || [];
  const sources = suggestion?.sources_used || [];

  return (
    <div className="contents" data-testid="ai-reply-panel">
      {/* Trigger row — sits flush above the composer. Compact + responsive:
          icon-only on very small screens, confidence pill always visible.
          The suggestion content lives in a Popover so big replies never
          push the chat or composer around. */}
      <Popover
        open={open}
        onOpenChange={(o) => {
          if (o && !suggestion && !loading) {
            requestSuggestion();
          }
          setOpen(o);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={loading || !conversationId}
            className="h-7 px-2 gap-1 border-wt-border text-wt-text-primary
                       hover:bg-wt-paper hover:text-wt-text-primary"
            data-testid="ai-suggest-reply-btn"
          >
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5 text-wt-gold" />
            )}
            <span className="text-[11px] font-medium">
              {loading ? 'Thinking…'
                : suggestion ? 'Suggestion'
                : 'Suggest reply'}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start" side="top" sideOffset={8}
          className="w-[min(440px,calc(100vw-1.5rem))] max-h-[60vh] overflow-y-auto p-3"
          data-testid="ai-suggestion-card"
        >
          {error ? (
            <div className="flex items-start gap-2 text-rose-600 text-xs"
                 data-testid="ai-suggestion-error">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" />
              <span>{error}</span>
              <button
                onClick={() => { setError(''); setOpen(false); }}
                className="ml-auto text-wt-text-muted hover:text-wt-text-primary"
                data-testid="ai-error-dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : suggestion ? (
            <>
              {/* Reply body */}
              <div
                className="text-sm text-wt-text-primary whitespace-pre-wrap leading-relaxed"
                data-testid="ai-suggested-reply-text"
              >
                {suggestion.suggested_reply}
              </div>

              {/* Source chips */}
              {sources.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ai-sources-row">
                  {sources.map(s => {
                    const meta = SOURCE_LABELS[s] || { icon: Sparkles, label: s };
                    const Icon = meta.icon;
                    return (
                      <span
                        key={s}
                        className="inline-flex items-center gap-1 text-[10px]
                                   px-1.5 py-0.5 rounded-full bg-white/60
                                   border border-wt-border text-wt-text-muted"
                        data-testid={`ai-source-${s}`}
                      >
                        <Icon className="h-2.5 w-2.5" />
                        {meta.label}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Safety flags */}
              {(flags.length > 0 || suggestion.needs_human) && (
                <div
                  className="mt-2 flex items-start gap-2 text-[11px] text-amber-800
                             bg-amber-50 border border-amber-200 rounded px-2 py-1.5"
                  data-testid="ai-safety-warnings"
                >
                  <ShieldAlert className="h-3.5 w-3.5 mt-0.5 flex-none text-amber-700" />
                  <div className="space-y-0.5">
                    {suggestion.needs_human && (
                      <div className="font-medium" data-testid="ai-needs-human-flag">
                        Review carefully — this conversation likely needs a human touch.
                      </div>
                    )}
                    {flags.length > 0 && (
                      <div>
                        Flags: {flags.map((f, i) => (
                          <Badge
                            key={`${f}-${i}`}
                            variant="outline"
                            className="ml-1 text-[9px] py-0 px-1 border-amber-300 text-amber-800"
                            data-testid={`ai-flag-${f}`}
                          >
                            {f}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="mt-3 flex items-center gap-2 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={requestSuggestion}
                  disabled={loading}
                  className="h-7 px-2 gap-1 text-[11px] text-wt-text-muted hover:text-wt-text-primary"
                  data-testid="ai-regenerate-btn"
                >
                  {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  Regenerate
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpen(false)}
                  className="h-7 text-xs text-wt-text-muted"
                  data-testid="ai-dismiss-btn"
                >
                  Dismiss
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    if (onInsertSuggestion && suggestion.suggested_reply) {
                      onInsertSuggestion(suggestion.suggested_reply);
                    }
                    setOpen(false);
                  }}
                  className="h-7 text-xs bg-wt-gold hover:bg-wt-gold/90 text-white"
                  data-testid="ai-insert-btn"
                >
                  Insert
                </Button>
              </div>
            </>
          ) : (
            <div className="text-xs text-wt-text-muted">
              {loading ? 'Generating suggestion…' : 'No suggestion yet.'}
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Confidence pill — sits next to the trigger when a suggestion
          exists, even when popover is closed. Compact, just a number. */}
      {suggestion && (
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${conf.bg} ${conf.text} whitespace-nowrap`}
          title={`${suggestion.confidence}% · ${conf.label}`}
          data-testid="ai-confidence-pill"
        >
          {suggestion.confidence}%
        </span>
      )}
    </div>
  );
}
